/**
 * Copyright (c) 2021-22 Amway
 * @author Mohit Chunawala
 */

import React from "react";
import Button from "../../shared-ui/button";
import ColumnResizer from "react-column-resizer";
import InputSection from "./input-section";
import InputField from "../../shared-ui/input-field";
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import ResultsSection from "./results-section";
import RulesSection from "./rules-section";
import { response } from "../../results-landing/mock-response";
import SaveTestCasePopup from "../../shared-ui/popup/admin-save-popup";
import DifferencePopup from "../../shared-ui/popup/difference-popup";
import AdminPopupComponent from "../../shared-ui/popup/admin-popup";
import { handleDeleteTestScenarioAPI } from "../../../services/adminToolServices/deleteTestScenario";
import { signOutAdmin } from "../../../utils/validateAdminSession";
import MessageStrip from "../../shared-ui/message-strip";
import { msalInstance } from "../../../core/authentication/entra/msal";

function getAdminEmail(): string {
  const accounts = msalInstance.getAllAccounts();
  if (!accounts?.length) return "";
  const acct = accounts[0];
  const claims: any = acct?.idTokenClaims || {};
  return claims?.email || claims?.preferred_username || acct?.username || "";
}

// Interface for Test Case Dashboard State
interface TestCaseDashboardState {
  isRulesVisiable: boolean;
  metaResponse: any;
  testCaseNameState: string;
  isPopUpShow: boolean;
  test_id: any;
  user: any;
  isOpenMessageStrip: boolean;
  deleteObjProps: any;
  isViewMode: boolean;
  isEditMode: boolean;
  creator: string;
  initialInput: any;
  initialResults: any;
  isLeavePopUp: boolean;
  inputs: any;
  isRunLoad: boolean;
  isApiFailed: boolean;
  ApiFailedMessage: string;
  testCaseStatus: any;
  testCaseStatusIsSet: any;
}

class TestCaseDashboard extends React.Component<any, TestCaseDashboardState> {
  node: any;
  constructor(props: any) {
    super(props);
    this.state = {
      isRulesVisiable: false,
      metaResponse: {},
      testCaseNameState: "",
      isPopUpShow: false,
      test_id: "",
      user: "",
      isOpenMessageStrip: false,
      deleteObjProps: {},
      isViewMode: false,
      isEditMode: false,
      creator: "",
      initialInput: {},
      initialResults: [],
      isLeavePopUp: false,
      inputs: {},
      isRunLoad: false,
      isApiFailed: false,
      ApiFailedMessage: "",
      testCaseStatus: null,
      testCaseStatusIsSet: null,
    };
  }

  setApiFailure = (
    err: any,
    fallback = "Something went wrong. Try again or start over"
  ) => {
    const msg = 
    err?.response?.errors?.[0]?.message ||
    err?.message ||
    err?.networkError?.message ||
    fallback;
    this.setState({ isApiFailed: true, ApiFailedMessage: msg, isRunLoad: false });
    console.error("Admin Test Dashboard API error:", err);
  };

  handleViewRules = (value: boolean) => {
    this.setState({
      isRulesVisiable: value,
    });
  };
  callBackResponse = (response: any, inputs?: any) => {
    this.setState({
      metaResponse: response,
      inputs: inputs,
      isRunLoad: false,
    });
  };
  callBackDifference = (status: boolean, statusIsSet: boolean) => {
    this.setState({
      testCaseStatus: status,
      testCaseStatusIsSet: statusIsSet,
    });
  };

  handleEditMode = (on: boolean) => {
    this.setState({
      isViewMode: !on, isEditMode: on
    });
  };

  handleViewMode = (testCaseName: string) => {
    const userName = getAdminEmail();
    // const oktaDetails: any = sessionStorage.getItem("okta-token-storage");
    // const parsedOktaData: any = JSON.parse(oktaDetails);
    // const userName: any = parsedOktaData?.idToken?.claims?.email;

    this.setState({
      testCaseNameState: testCaseName,
      deleteObjProps: {
        test_id: testCaseName,
        user: userName,
      },
      isViewMode: true,
      isEditMode: false,
    });
  };

  handleRunload = (value: any) => {
    this.setState({
      isRunLoad: value,
    });
  };
  componentDidMount = () => {
    const email = getAdminEmail();
    if (email) this.setState({ user: email, creator: email});

    if (this.props.show) {
      const accounts = msalInstance.getAllAccounts();
      if (!accounts || accounts.length === 0) {
        signOutAdmin();
      }
      document.addEventListener("click", this.handleOutsideClick, true);
    }

    const isEditRaw = localStorage.getItem("isEdit");
    const isEditStorage = isEditRaw === "true";

    let metaData: any = [];
    let answersData: any = {};
    let testCaseName: any = localStorage.getItem("testCaseName") || "";

    try { metaData = JSON.parse(localStorage.getItem("meta_result") || "[]"); } catch {}
    try { answersData = JSON.parse(localStorage.getItem("answersObj") || "{}"); } catch {}

    if (this.props?.history?.location?.state) {
      localStorage.setObj(
        "answersObj",
        this.props.history.location.state.initialInput);

      const deleteObjProps = this.props.history.location.state;
      this.setState({
        deleteObjProps,
        testCaseNameState: deleteObjProps.test_id,
        isViewMode: !isEditStorage,
        isEditMode: isEditStorage,
        creator: deleteObjProps.creator,
        initialInput: deleteObjProps.initialInput,
        initialResults: deleteObjProps.initialOutput,
      });
    } else if (isEditStorage && testCaseName) {
      this.setState({
        isEditMode: true,
        testCaseNameState: testCaseName,
      });
    } else if (
      metaData.length &&
      testCaseName &&
      Object.keys(answersData).length
    ) {
      this.handleViewMode(testCaseName);
      this.callBackResponse(metaData, answersData);
    }
  };

  componentDidUpdate = (prevProps: any) => {
    if (this.props.show !== prevProps.show) {
      if (this.props.show) {
      const accounts = msalInstance.getAllAccounts();
      if (!accounts || accounts.length === 0) {
        signOutAdmin();
      }
      // attach/remove event handler
      document.addEventListener("click", this.handleOutsideClick, true);
    } else {
      document.removeEventListener("click", this.handleOutsideClick, true);
      }
    }
  };

  componentWillUnmount(): void {
    document.removeEventListener("click", this.handleOutsideClick, true);
  }

  handleOutsideClick = (e: any) => {
    // ignore clicks on the component itself
    if (this.node) {
      if (this.node.contains(e.target)) {
        return;
      }
      this.handleClose();
    }
  };

  /**
   * This method is used to delete test case
   *
   */
handleDeleteTestCase = async () => {
  try {
    const { deleteObjProps } = this.state;

    const deleteTestCaseObj = {
      test_id: deleteObjProps.test_id,
      user: deleteObjProps.user,
    };

    localStorage.removeItem("answersObj");

    const response: any = await handleDeleteTestScenarioAPI(deleteTestCaseObj, this.props);

    if (response?.error === true) {
      this.setApiFailure(new Error(response?.message || "Delete failed"));
      return;
    }

    this.setState(
      {
        isPopUpShow: false,
        isOpenMessageStrip: !this.state.isOpenMessageStrip,
      },
      () => {
        this.props.history.push({
          pathname: "/admin/testlist",
          state: { isOpenMessageStrip: this.state.isOpenMessageStrip },
        });
      }
    );
  } catch (e) {
    this.setApiFailure(e);
  }
};

  /**
   * This method is used to back arrow click
   *
   */
  handleBack = () => {
    if (this.state.isViewMode === true) {
      localStorage.removeItem("answersObj");
      localStorage.removeItem("meta_result");
      localStorage.removeItem("testCaseName");
      localStorage.removeItem("isEdit");
      this.props.history.push("/admin/testlist");
    }
    this.setState({
      isLeavePopUp: true,
    });
  };

  /**
   * This method is used to Discard data on back button
   *
   */
  handleDiscard = () => {
    this.setState({
      isLeavePopUp: false,
    });
    localStorage.removeItem("answersObj");
    localStorage.removeItem("meta_result");
    localStorage.removeItem("testCaseName");
    localStorage.removeItem("isEdit");
    this.props.history.push("/admin/testlist");
  };

  /**
   * This method is used to closed popUp
   *
   */
  handleClose = () => {
    this.setState({
      isPopUpShow: false,
      isLeavePopUp: false,
    });
  };

  render() {
    const {
      isRulesVisiable,
      metaResponse,
      isPopUpShow,
      testCaseNameState,
      isViewMode,
      isEditMode,
      creator,
      initialInput,
      initialResults,
      isLeavePopUp,
      inputs,
      isApiFailed,
      testCaseStatus,
      testCaseStatusIsSet,
    } = this.state;

    return (
      <div className="testCase-dashboard">
        {/* Delete Popup Start */}
        {isPopUpShow && (
          <AdminPopupComponent
            open={isPopUpShow}
            close={this.handleClose}
            onDelete={this.handleDeleteTestCase}
            buttonName={"Delete"}
            cancleButtonName={"Cancel"}
            heading={"Hold on"}
            title={
              "Are you sure you want to delete this Test Case? This will also delete all its previous versions as well."
            }
          />
        )}
        {/* Delete Admin Popup End */}
        {this.state.isApiFailed && (
          <MessageStrip
            messageText={
              this.state.ApiFailedMessage ||
              "Something went wrong. Try again or start over"
            }
            error
          />
        )}
        {/* Back Popup Start */}
        {isLeavePopUp && (
          <AdminPopupComponent
            open={isLeavePopUp}
            close={this.handleClose}
            onDelete={this.handleDiscard}
            buttonName={"Discard"}
            cancleButtonName={"Cancel"}
            heading={"Hold on"}
            title={
              "Are you sure you want to leave? You’ll lose any unsaved changes."
            }
          />
        )}
        {/* Back Popup End */}

        {/* Create Test case Heading Start */}
        <div className="create-testCase-wrapper">
          <div className="back-area" onClick={this.handleBack}>
            <div className="icon back-btn"></div>
            <span className="label">Back</span>
          </div>
          <div className="heading">
            {" "}
            {testCaseNameState ? testCaseNameState : "Create New Test Case"}
          </div>
          {!isEditMode && testCaseNameState && (
            <div className="edit-area" style={{position: "relative", zIndex: 2 }}>
              <Button
                onButtonClick={() => {
                  this.setState(
                    {
                      isEditMode: true,
                    },
                    () => {
                      localStorage.setItem("isEdit", "true");
                    }
                  );
                }}
                name={"Edit"}
                className={"app-btn"}
              />
              <div
                className="delete-btn"
                onClick={() => {
                  this.setState({
                    isPopUpShow: true,
                  });
                }}
              >
                Delete
              </div>
            </div>
          )}
        </div>
        {/* Create Test case Heading Ends */}
        {/* Middle Area Start */}
        <div className="middle-area">
          <div className="case-wrapper">
            <table className="test-case-table">
              <tbody>
                <tr>
                  <td>
                    {" "}
                    {/* Input Area Start */}
                    <InputSection
                      {...this.props}
                      handleViewRules={(isRulesVisiable: boolean) =>
                        this.handleViewRules(isRulesVisiable)
                      }
                      isRulesVisiable={isRulesVisiable}
                      callBack={(response: any, inputs?: any) => {
                        this.callBackResponse(response, inputs);
                      }}
                      callBackDifference={(
                        status: boolean,
                        statusIsSet: boolean
                      ) => {
                        this.callBackDifference(status, statusIsSet);
                      }}
                      isViewMode={isViewMode}
                      isEditMode={isEditMode}
                      handleEditMode={(bool: boolean) => {
                        this.handleEditMode(bool);
                      }}
                      handleRunLoad={(value: any) => this.handleRunload(value)}
                      testCaseName={testCaseNameState}
                    />
                    {/* <InputSection/> */}
                    {/* Input Area Ends */}
                  </td>
                  {/* <ColumnResizer className="columnResizer" minWidth={420} /> */}
                  {isRulesVisiable && (
                    <>
                      <RulesSection
                        {...this.props}
                        handleViewRules={(isRulesVisiable: boolean) =>
                          this.handleViewRules(isRulesVisiable)
                        }
                        metaResponseProps={metaResponse}
                        isEditMode={isEditMode}
                        isRunLoad={this.state.isRunLoad}
                      />
                      {/* <ColumnResizer className="columnResizer" minWidth={420} /> */}
                    </>
                  )}
                  <td>
                    {" "}
                    <ResultsSection
                      {...this.props}
                      metaResponseProps={metaResponse}
                      inputProps={inputs}
                      handleViewMode={(testCaseName: string) =>
                        this.handleViewMode(testCaseName)
                      }
                      isViewMode={isViewMode}
                      isEditMode={isEditMode}
                      testCaseName={testCaseNameState}
                      creator={creator}
                      initialInput={initialInput}
                      initialResults={initialResults}
                      isRunLoad={this.state.isRunLoad}
                      testCaseStatus={testCaseStatus}
                      testCaseStatusIsSet={testCaseStatusIsSet}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        {/* Middle Area Ends */}
      </div>
    );
  }
}

export default TestCaseDashboard;
